'use client';

import { number } from '@frameio/i18n';
import { Box, Button2, InlineButton, Text } from '@frameio/vapor';
import {
  IconArrowDownRight,
  IconBeaker,
  IconCustomFieldLink,
  IconEdit,
  IconInfo,
  IconLightbulb,
  IconPlay,
  IconShare,
} from '@frameio/vapor-icons';
import { BetaChip } from '../../../components/BetaChip';
import type {
  IconToken,
  TooltipAction,
  TooltipActionAlignment,
  TooltipLinkContent,
  TooltipMedia,
} from '../../stores/tooltipStore.types';
import TEXT from './generated/TEXT/OnboardingTooltipBody';
import styles from './OnboardingTooltipContainer.module.css';

TEXT.DEFINE({
  /** Step counter in multi-step onboarding tooltips (e.g. "1 of 2") */
  stepProgress: (current: number, total: number) =>
    `${number(current)} of ${number(total)}`,
});

type Props = {
  title: string;
  description?: string;
  icon?: IconToken;
  showBetaChip?: boolean;
  media?: TooltipMedia;
  link?: TooltipLinkContent;
  actions?: TooltipAction[];
  actionAlignment?: TooltipActionAlignment;
  onAction: (action: TooltipAction) => void;
  stepInfo?: { current: number; total: number };
};

const ICONS = {
  IconArrowDownRight,
  IconBeaker,
  IconEdit,
  IconInfo,
  IconLightbulb,
  IconPlay,
  IconShare,
  IconCustomFieldLink,
} as const;

export function OnboardingTooltipBody({
  title,
  description,
  icon,
  showBetaChip,
  media,
  link,
  actions,
  actionAlignment,
  onAction,
  stepInfo,
}: Props) {
  const IconComp = icon ? (ICONS as Record<string, any>)[icon] : undefined;
  const iconNode = IconComp ? (
    <IconComp color="primary" size={24} />
  ) : undefined;

  const allActions = actions ?? [];

  return (
    <div className={styles.contentContainer}>
      <div className={styles.textContent}>
        {showBetaChip ? (
          <div className={styles.betaChipRow}>
            <BetaChip />
          </div>
        ) : null}
        {iconNode ? (
          <div className={styles.headerIconRow}>
            <span className={styles.headerIcon}>{iconNode}</span>
          </div>
        ) : null}
        <Text color="bodyStark" fontWeight="medium">
          {title}
        </Text>
        {description ? (
          <Text color="secondary" style={{ whiteSpace: 'pre-line' }}>
            {description}
          </Text>
        ) : null}

        {link && link.href && link.label ? (
          <div className={styles.linkButton}>
            <InlineButton
              onPress={() => {
                window.open(
                  link.href,
                  link.openInNewTab ? '_blank' : '_self',
                  'noopener,noreferrer'
                );
              }}
            >
              <Box alignItems="center" display="flex" gap={4}>
                {(() => {
                  const LinkIconComp = (ICONS as Record<string, any>)[
                    link.icon
                  ];
                  return LinkIconComp ? (
                    <LinkIconComp className={styles.linkIcon} size={16} />
                  ) : null;
                })()}
                <Text color="link" variant="linkSmall">
                  {link.label}
                </Text>
              </Box>
            </InlineButton>
          </div>
        ) : null}
      </div>

      {media ? (
        <div className={styles.mediaContainer}>
          {media.mediaType === 'image' ? (
            <img
              alt={media.alt}
              className={styles.media}
              src={media.src}
              style={{
                aspectRatio: media.aspectRatio
                  ? String(media.aspectRatio)
                  : undefined,
              }}
            />
          ) : (
            // Videos in tooltips won't have audio, so we won't need captions
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              autoPlay={media.autoplay ?? true}
              className={styles.media}
              controls={false}
              loop={media.loop ?? true}
              muted={media.muted ?? true}
              poster={media.poster}
            >
              <source src={media.src} />
            </video>
          )}
        </div>
      ) : null}

      {(allActions.length > 0 || stepInfo) && (
        <div
          className={styles.actionContainer}
          data-alignment={stepInfo ? undefined : actionAlignment}
        >
          {stepInfo && (
            <Text color="tertiary" variant="secondary">
              {TEXT.stepProgress(stepInfo.current + 1, stepInfo.total)}
            </Text>
          )}
          {allActions.length > 0 && (
            <div className={styles.actionButtons}>
              {allActions.map((act, index) => {
                const isCommand = act.kind === 'command';
                const isPrevious = isCommand && act.command === 'previous';
                const isNext = isCommand && act.command === 'next';
                const isDismiss = isCommand && act.command === 'dismiss';

                return (
                  <Button2
                    key={index}
                    isDisabled={
                      (isPrevious && stepInfo?.current === 0) ||
                      (isNext &&
                        stepInfo &&
                        stepInfo.current === stepInfo.total - 1)
                    }
                    onClick={() => onAction(act)}
                    variant={isNext || isDismiss ? 'primary' : 'secondary'}
                  >
                    {act.label}
                  </Button2>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
